/**
 * VariantTriggerRulesDrawer — per-variant trigger-rule editor hosted
 * inside the App Integrations tab (#1184, Slice 1 of meta #1183).
 *
 * Replaces the standalone variant Automation page's Triggers section.
 * The user clicks a variant row in the App Integrations tab's GitHub
 * card → this drawer slides in from the right with the variant's
 * existing rules at the top and an inline add/edit form below.
 *
 * **What lives here vs. the modal.** The page used to mount
 * `TriggerRuleEditorModal` (a nested dialog) when the operator clicked
 * Add. That stacked a modal on top of a page-level navigation, which
 * the new drawer-based IA replaces. The shared `TriggerRuleEditorForm`
 * — extracted from the modal in the same slice — renders inline inside
 * the drawer's body so there is no nested overlay.
 *
 * **Data flow.** The drawer fan-outs the existing variant→templates→rules
 * read (same path the deleted Automation page used). The backend's
 * `/trigger-rules` endpoint takes `?templateId=`, not `?variantId=`;
 * `runTemplatesAPI.listForVariant` returns this variant's Replay
 * templates, then `triggerRulesAPI.listForTemplates` fans out one call
 * per template. Identical to pre-#1184 behavior; a future
 * `?variantId=` filter on the backend would collapse the fan-out
 * without changing this component.
 *
 * **Vocabulary.** The drawer renders "Suite selection" as the UI label
 * for the template picker (per the #1184 mockup). The underlying
 * entity is still a `RunTemplate` with `kind: 'replay'` — code
 * identifiers don't change.
 *
 * **Repo binding required to add.** A rule needs a repo identifier +
 * GitHub workspace integration id. Both come from the App's per-app
 * GitHub binding (#1154) + the workspace's GitHub CI integration.
 * When neither resolves, the Add CTA is disabled with a hint pointing
 * at the GitHub card above the drawer; the existing rules still
 * render so the operator can edit / delete them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Info, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@/components/ui'
import { TriggerRuleEditorForm } from '@/components/TriggerRuleEditorForm'
import { RunTemplatePoolList } from '@/components/RunTemplatePoolList'
import { useAppContext } from '@/hooks/useAppContext'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import {
  ciIntegrationsAPI,
  runTemplatesAPI,
  triggerRulesAPI,
  type CiIntegration,
} from '@/lib/api'
import {
  eventPillClass,
  eventPillLabel,
  ruleEventFragments,
} from '@/lib/triggerRules'
import type { TriggerRule } from '@/types'

interface RunTemplateLike {
  id?: string
  _id?: string
  name: string
  variantId?: string
}

export interface VariantTriggerRulesDrawerProps {
  appId: string
  variantId: string
  /** The App's bound repo identifier (Slice 2 / #1154). When set, the
   * drawer can build a preset for the editor form so a freshly-bound
   * app with no rules still pre-fills a workable payload. When unset,
   * Add is disabled with a hint. */
  githubRepoIdentifier: string | undefined
  onClose: () => void
}

export function VariantTriggerRulesDrawer({
  appId,
  variantId,
  githubRepoIdentifier,
  onClose,
}: VariantTriggerRulesDrawerProps) {
  const { allVariants } = useAppContext()
  const confirm = useConfirm()
  const toast = useToast()

  const variant = useMemo(
    () => allVariants.find((v) => v.id === variantId) ?? null,
    [allVariants, variantId],
  )

  // Cross-app guard. `allVariants` is workspace-wide; a crafted /
  // stale `?variantConfig=<vid>` URL could point at a variant
  // belonging to a DIFFERENT app in the same workspace. Without this
  // gate the drawer would display that other variant's name while
  // editing rules under the current `appId` context — a confusing
  // surface that could let the user mutate rules on a variant they
  // don't see in the surrounding oversight. When the IDs don't match
  // we close the drawer; the URL strips back to a clean state via
  // the caller's `onClose` and the user lands in the App Integrations
  // tab proper.
  useEffect(() => {
    if (variant && variant.applicationId !== appId) {
      onClose()
    }
  }, [variant, appId, onClose])

  const [templates, setTemplates] = useState<RunTemplateLike[]>([])
  const [rules, setRules] = useState<TriggerRule[]>([])
  const [ciIntegrations, setCiIntegrations] = useState<CiIntegration[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Editor state: null = list view; { mode: 'add' } = the inline
  // add form is open; { mode: 'edit', rule } = editing an existing
  // row. Mirrors the pattern the old VariantAutomation page used so
  // the behavior is identical.
  const [editorState, setEditorState] = useState<
    | { mode: 'add' }
    | { mode: 'edit'; rule: TriggerRule }
    | null
  >(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Only the settings CI rules may use (#2175 slice 4). This one
      // argument is what narrows the editor's picker too, since its options
      // are derived from this list — without it a rule could point at the
      // settings someone edits before a manual run, and that edit would
      // change what the next commit runs.
      const templatesRaw = (await runTemplatesAPI.listForVariant(variantId, 'ci')) as RunTemplateLike[]
      const templateIds = templatesRaw
        .map((t) => t.id ?? (t._id ? String(t._id) : ''))
        .filter(Boolean)
      const ruleList = await triggerRulesAPI.listForTemplates(templateIds)
      setTemplates(templatesRaw)
      setRules(ruleList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trigger rules')
    } finally {
      setLoading(false)
    }
    // Silent in parallel — failure leaves Add disabled, which is the
    // same as the App not having a binding (the operator's recovery
    // path points at the GitHub card above the drawer either way).
    void ciIntegrationsAPI
      .list()
      .then(setCiIntegrations)
      .catch(() => setCiIntegrations([]))
  }, [variantId])

  useEffect(() => {
    void load()
  }, [load])

  const templateLookup = useMemo(() => {
    const out: Record<string, string> = {}
    for (const t of templates) {
      const id = t.id ?? (t._id ? String(t._id) : '')
      if (id) out[id] = t.name
    }
    return out
  }, [templates])

  // The template picker inside the editor needs the variant's templates
  // as `{ value, label }` options. Pre-loaded so the editor doesn't fire
  // its own workspace-scoped fetch — the drawer already has the
  // variant-scoped list.
  const templateOptions = useMemo(
    () =>
      templates
        .map((t) => ({
          value: t.id ?? (t._id ? String(t._id) : ''),
          label: t.name,
        }))
        .filter((o) => o.value),
    [templates],
  )

  // Preset for the editor's create path. Requires both a bound repo
  // identifier on the App AND a workspace GitHub integration id (ADR
  // 0019 locks exactly one GitHub integration per workspace, so the
  // first row is the right read). Returns null when either piece is
  // missing — the editor's create path won't fire and the Add CTA
  // shows the unbound hint.
  const presetRepo = useMemo(() => {
    if (!githubRepoIdentifier || !ciIntegrations) return null
    const integration = ciIntegrations.find((i) => i.provider === 'github')
    if (!integration) return null
    return {
      provider: 'github' as const,
      integrationId: integration.id,
      repoIdentifier: githubRepoIdentifier,
    }
  }, [githubRepoIdentifier, ciIntegrations])

  function handleSaved(saved: TriggerRule) {
    setEditorState(null)
    setRules((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id)
      return idx >= 0
        ? prev.map((r) => (r.id === saved.id ? saved : r))
        : [saved, ...prev]
    })
  }

  async function handleDelete(rule: TriggerRule) {
    const ok = await confirm({
      title: 'Delete trigger rule?',
      description:
        'This rule will stop firing on matching events. You can recreate it later.',
      confirmLabel: 'Delete rule',
      destructive: true,
    })
    if (!ok) return
    setDeletingId(rule.id)
    try {
      await triggerRulesAPI.delete(rule.id)
      setRules((prev) => prev.filter((r) => r.id !== rule.id))
    } catch (err) {
      toast({
        title: 'Failed to delete trigger rule',
        description: toUserMessage(
          err,
          "Couldn't delete the trigger rule. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setDeletingId(null)
    }
  }

  const variantName = variant?.name ?? 'Variant'
  const ariaLabel = `Trigger rules for ${variantName}`
  const canAdd = !!presetRepo
  const initialRule =
    editorState?.mode === 'edit' ? editorState.rule : undefined

  return (
    <Drawer open onOpenChange={(open) => !open && onClose()}>
      <DrawerContent ariaLabel={ariaLabel} data-testid="variant-trigger-rules-drawer">
        <DrawerHeader
          eyebrow="Trigger rules"
          title={variantName}
          subtitle="On successful deployments to GitHub → run a suite against the deploy URL"
        />
        <DrawerBody>
          {/* Platform callout — explains the deployment_status fan-in
              so customers reading this drawer for the first time
              understand the rule fires automatically off their existing
              PaaS deploys. Always visible; sits above the rule list
              regardless of loading / error state because the educational
              context is independent of fetch state. The list of named
              providers is illustrative, not exhaustive — anything that
              posts GitHub deployment_status events will fire matching
              rules. */}
          <div
            className="mb-3 flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
            data-testid="drawer-platform-callout"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <p className="leading-relaxed">
              <strong className="font-semibold">Works with your deploy platform.</strong>{' '}
              When Vercel, Netlify, Render, Cloudflare Pages, AWS Amplify,
              or your own GitHub Actions deploy reports back to GitHub,
              OttoTester fires matching rules and runs the suite against
              the URL your platform just deployed to — not this variant's
              configured URL.
            </p>
          </div>
          {loading ? (
            <div className="space-y-2" aria-label="Loading trigger rules">
              {[1, 2].map((i) => (
                <div key={i} className="h-12 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </div>
          ) : (
            <div className="space-y-3">
              {rules.length === 0 && !editorState && (
                <div
                  className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
                  data-testid="drawer-rules-empty"
                >
                  No trigger rules for this variant yet.
                </div>
              )}

              {rules.length > 0 && (
                <ul
                  className="space-y-2"
                  data-testid="drawer-rules-list"
                >
                  {rules.map((rule) => (
                    <RuleRow
                      key={rule.id}
                      rule={rule}
                      templateLookup={templateLookup}
                      isDeleting={deletingId === rule.id}
                      onEdit={() => setEditorState({ mode: 'edit', rule })}
                      onDelete={() => void handleDelete(rule)}
                    />
                  ))}
                </ul>
              )}

              {/* The settings this connection's rules may use (#2175 slice
                  4). Sits under the rules because a rule is the thing being
                  built and these are what it picks from. */}
              <RunTemplatePoolList
                variantId={variantId}
                applicationId={appId}
                pool="ci"
                heading="Run settings these rules can use"
                emptyHint="No settings here yet. Add one, then a rule can point at it."
                usageCountFor={(id) => rules.filter((r) => r.templateId === id).length}
                usageNoun="rule"
                onChanged={() => void load()}
              />

              {editorState ? (
                <div
                  className="rounded-md border border-primary/40 bg-primary/5 p-3"
                  data-testid="drawer-rule-editor"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    {editorState.mode === 'add' ? 'New rule' : 'Edit rule'}
                  </p>
                  <TriggerRuleEditorForm
                    initialRule={initialRule}
                    presetRepo={presetRepo ?? undefined}
                    compact
                    templateLabel="Suite selection"
                    templateRequired
                    saveLabel="Save rule"
                    templateOptionsOverride={templateOptions}
                    onCancel={() => setEditorState(null)}
                    onSaved={handleSaved}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => canAdd && setEditorState({ mode: 'add' })}
                  disabled={!canAdd}
                  data-testid="drawer-add-rule"
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-3 text-sm text-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-background"
                  title={
                    canAdd
                      ? undefined
                      : 'Bind this app to a repo on the GitHub card first, then come back here to add rules.'
                  }
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add trigger rule
                </button>
              )}

              {!canAdd && !editorState && rules.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Bind this app to a repo on the GitHub card above, then come back here to add rules.
                </p>
              )}
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

interface RuleRowProps {
  rule: TriggerRule
  templateLookup: Record<string, string>
  isDeleting: boolean
  onEdit: () => void
  onDelete: () => void
}

function RuleRow({ rule, templateLookup, isDeleting, onEdit, onDelete }: RuleRowProps) {
  const [prefix, environment, suffix] = ruleEventFragments(rule)
  const templateName = templateLookup[rule.templateId]
  return (
    <li
      className="rounded-md border border-border bg-background p-3"
      data-testid="drawer-rule-row"
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${eventPillClass(rule)}`}
        >
          {eventPillLabel(rule)}
        </span>
        <div className="ml-auto flex gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onEdit}
            title="Edit"
            aria-label="Edit trigger rule"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onDelete}
            disabled={isDeleting}
            title="Delete"
            aria-label="Delete trigger rule"
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <p className="text-sm">
        {prefix}
        {environment && <em className="not-italic font-medium">{environment}</em>}
        {suffix}
        {templateName && (
          <>
            {' '}
            → run <strong className="font-semibold">{templateName}</strong>
          </>
        )}
      </p>
    </li>
  )
}
